import React, { createContext, useContext, useReducer } from "react";
import moment from "moment";

import { UserAttributes } from "../attributes/user-attributes";
import { BaseState } from "./CommonContextTypes";
import { UserService } from "../services/UserService";
import User from "../models/User";
import { BusinessAttributes } from "../attributes/business-attributes";
import { BusinessBranchAttributes } from "../attributes/business-branch-attributes";

const USER_KEY_DATA = "user";
export const TOKEN_KEY_DATA = "access_token";
export const SESSION_EXPIRES_AT_KEY_DATA = "session_expires_at";

interface UserState extends BaseState {
  access_token?: string;
  session_expires_at?: any;
  user?: UserAttributes;
}

export enum UserActionTypeEnum {
  loading,
  loaded,
  login,
  logout,
  addBusiness,
  addBranch,
  updateBusiness,
}

interface UserAction {
  type: UserActionTypeEnum;
  data?: UserState | BusinessAttributes | BusinessBranchAttributes;
}

const UserContext = createContext<UserState>({
  loading: true,
});
const UserDispatchContext = createContext<Function>(() => {});

function UserReducer(state: UserState, action: UserAction): UserState {
  let user = state.user;

  switch (action.type) {
    case UserActionTypeEnum.loading:
      return { ...state, loading: true };
    case UserActionTypeEnum.loaded:
      return { ...state, loading: false };
    case UserActionTypeEnum.login:
      console.dir("User Logged In...");

      // Store into local storage
      setUserData(action.data as UserState);
      return {
        ...state,
        ...getUserState(),
      };
    case UserActionTypeEnum.logout:
      console.dir("User Logged out...");

      // Clear local storage
      clearUser();
      return { ...state, ...getUserState() };
    case UserActionTypeEnum.addBusiness:
      console.dir("New business saved...");

      user.businesses.unshift(action.data as BusinessAttributes);
      setUserData({ ...state, user: user });

      return { ...state, ...getUserState() };
    case UserActionTypeEnum.addBranch:
      console.dir("New managed branch saved...");

      user.branches.unshift(action.data as BusinessBranchAttributes);
      setUserData({ ...state, user: user });

      return { ...state, ...getUserState() };
    case UserActionTypeEnum.updateBusiness:
      console.dir("Updating business ...");

      const newData = action.data as BusinessAttributes;
      let businesses = user.businesses.filter((item) => {
        return item.id !== newData.id;
      });

      businesses.unshift(newData);
      user.businesses = businesses;
      setUserData({ ...state, user: user });

      return { ...state, ...getUserState() };
    default:
      return state;
  }
}

export const UserProvider: React.FC = ({ children }) => {
  const [state, dispatch] = useReducer(UserReducer, getUserState());

  return (
    <UserContext.Provider value={state}>
      <UserDispatchContext.Provider value={dispatch}>
        {children}
      </UserDispatchContext.Provider>
    </UserContext.Provider>
  );
};

// External hooks

export function userIsLoggedIn(): boolean {
  let { user, access_token } = getUserState();

  return Boolean(access_token !== null) && Boolean(user !== null);
}

export function useAuthenticationContext(): [User, string, Function] {
  let { user, access_token } = useUserState();

  return [
    user !== null ? User.fromJson(JSON.parse(JSON.stringify(user))) : null,
    access_token,
    useUserDispatch(),
  ];
}

export function useAuthSession(): number {
  let { session_expires_at } = useUserState();

  return parseInt(session_expires_at);
}

export function useUserState(): UserState {
  return useContext(UserContext);
}

export function useUserDispatch() {
  return useContext(UserDispatchContext);
}

// Utilities

function setUserData(data: UserState): void {
  const { access_token, user } = data;
  let dateData = localStorage.getItem(SESSION_EXPIRES_AT_KEY_DATA);
  dateData = dateData !== null && dateData !== undefined ? dateData : moment().add(2, 'hours').unix().toString();

  localStorage.setItem(SESSION_EXPIRES_AT_KEY_DATA, dateData);
  localStorage.setItem(USER_KEY_DATA, JSON.stringify(user));
  localStorage.setItem(TOKEN_KEY_DATA, JSON.stringify(access_token));
}

export function getUserState(): UserState {
  let dateData = localStorage.getItem(SESSION_EXPIRES_AT_KEY_DATA);

  return {
    user: getSessionData(USER_KEY_DATA),
    access_token: getSessionData(TOKEN_KEY_DATA),
    session_expires_at: dateData,
    loading: false,
  };
}

function getSessionData(key: string) {
  let json = localStorage.getItem(key);

  if (json !== null && json !== undefined) {
    return JSON.parse(json);
  }

  return null;
}

export function clearUser(): void {
  localStorage.clear();
}

// Remote request

export const remoteAuthUserRequest = async (accessToken: string, dispatch: Function): Promise<User | null> => {
  // Store token
  const authResponse = await UserService.authUser(accessToken);
  let user: User = null;

  if (authResponse.isSuccess && authResponse.data.code === 100) {
    // Store token into session storage or local storage
    user = User.fromJson(JSON.parse(JSON.stringify(authResponse.data.data)));

    dispatch({
      type: UserActionTypeEnum.login,
      data: {
        access_token: JSON.parse(JSON.stringify(accessToken)),
        user: JSON.parse(JSON.stringify(user)),
      },
    });
  } else {
    if (authResponse.httpCode === 401) {
      dispatch({
        type: UserActionTypeEnum.logout,
      });
    }

    user = null;
  }

  return user;
};
